import React from 'react';
import { DefaultButton, IconButton, Panel, PanelType, Spinner, SpinnerSize, Text } from '@fluentui/react';
import { TokenContext } from '../../models/Storefront';
import { useGetOpportunityTokensQuery } from '../../store/endpoints/StorefrontSettingsEndpoints';

/**
 * "Available Tokens" for any field that accepts {{{tokens}}} — a button, and a panel listing every
 * token for that context with a copy button beside it.
 *
 * The list comes from the API's token catalog rather than being restated per screen, so it cannot
 * drift from the server-side builder that actually renders them. Each context names one builder;
 * pick the one belonging to the thing being edited.
 *
 * Renders nothing for a context that resolves to no tokens, because an empty "Available Tokens"
 * panel reads as "still loading" rather than "there are none".
 */
interface ITokenReferenceButtonProps {
    context: TokenContext;
    /** Panel heading and button tooltip; defaults suit an email body. */
    title?: string;
    /** One line above the list saying where these get substituted. */
    description?: string;
    /** Icon-only suits a label slot; text suits sitting on its own above a field. */
    iconOnly?: boolean;
}

const TokenReferenceButton: React.FC<ITokenReferenceButtonProps> = (props) => {
    const tokens = useGetOpportunityTokensQuery(props.context);
    const [isOpen, setIsOpen] = React.useState(false);
    const [copied, setCopied] = React.useState<string | undefined>();

    const copy = (token: string) => {
        navigator.clipboard?.writeText(token);
        setCopied(token);
        window.setTimeout(() => setCopied((current) => (current === token ? undefined : current)), 1500);
    };

    const items = (tokens.data?.items ?? []).filter((t) => t.key);
    const loading = tokens.isLoading || tokens.isFetching;
    if (!loading && items.length === 0) return null;

    const title = props.title ?? 'Available Tokens';

    return (
        <>
            {props.iconOnly
                ? <IconButton iconProps={{ iconName: 'Tag' }} title={title} ariaLabel={title} onClick={() => setIsOpen(true)} />
                : <DefaultButton iconProps={{ iconName: 'Tag' }} text={title} onClick={() => setIsOpen(true)} />}
            <Panel
                isOpen={isOpen}
                onDismiss={() => setIsOpen(false)}
                type={PanelType.medium}
                headerText={title}
                closeButtonAriaLabel='Close'
                isBlocking={false}
                isLightDismiss={false}
            >
                <Text variant='small' block style={{ color: '#605e5c', marginBottom: 8 }}>
                    {props.description ?? 'Copy a token into the field; it is replaced with the corresponding value when the message is sent.'}
                </Text>
                {loading && <Spinner size={SpinnerSize.small} label='Loading tokens…' labelPosition='right' />}
                {!loading && items.map((t) => {
                    const token = `{{{${t.key}}}}`;
                    return (
                        <div key={t.key} style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
                            <IconButton
                                iconProps={{ iconName: copied === token ? 'CheckMark' : 'Copy' }}
                                title='Copy token'
                                ariaLabel='Copy token'
                                styles={{ root: { height: 24, width: 24 }, icon: { fontSize: 12 } }}
                                onClick={() => copy(token)}
                            />
                            <code style={{ background: '#f3f2f1', padding: '2px 6px', borderRadius: 3, fontSize: 12, whiteSpace: 'nowrap' }}>{token}</code>
                            <span style={{ color: '#605e5c', fontSize: 12 }}>{t.description}</span>
                        </div>
                    );
                })}
            </Panel>
        </>
    );
};

export default TokenReferenceButton;
